<template>
  <div>
    <div class="nav-section">
      <a
        id="settings-engineers-hkl-user-detail-back"
        class="nav-back"
        @click="$router.go(-1)"
      >
        {{ $t('back') }}
      </a>
    </div>
    <div class="user-detail-wrapper">
      <div class="row">
        <div class="small-6 medium-6 large-6 columns">
          <label class="emphasis">{{ $t('user.fullName') }}:</label>
          <label class="label-content">{{ userName(pendingUpdateUser) }}</label>
        </div>

        <div class="small-6 medium-6 large-6 columns user-detail-header-right">
          <div class="user-detail-header-top">
            <div class="last-login-info">
              <label class="emphasis">{{ $t('lastLogin') }}:</label>
              <label class="label-content">{{ formattedLastLoginAt | isEmptyValue }}</label>
            </div>
            <div class="controls right-adjusted header-controls">
              <HKLButton
                v-if="aclHelper.getSettingsUpdateAccess() && !allowEdit"
                color="orange"
                action="notify"
                @click="() => notify()"
              />
              <HKLButton
                v-if="aclHelper.getSettingsUpdateAccess() && !allowEdit"
                color="orange"
                action="edit"
                @click="enableEdit()"
              />
              <HKLButton
                v-if="aclHelper.getSettingsUpdateAccess() && allowEdit"
                color="darkblue"
                action="cancel"
                @click="discardChange()"
              />
              <HKLButton
                v-if="aclHelper.getSettingsUpdateAccess() && allowEdit"
                action="save"
                color="orange"
                @click="save()"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="small-6 medium-6 large-6 columns">
          <label class="emphasis">{{ $t('username') }}:</label>
          <label class="label-content username-content">{{ pendingUpdateUser.username }}</label>
          <label class="emphasis">{{ $t('position') }}:</label>
          <label class="label-content position-content">{{ pendingUpdateUser.title || 'N/A' }}</label>
          <label class="emphasis">{{ $t('userType.name') }}:</label>
          <label class="label-content">{{ $t(userType) }}</label>
        </div>

        <div class="small-6 medium-6 large-6 columns">
          <div
            id="settings-engineers-hkl-user-detail-status"
            class="large-8 medium-8 small-8 columns"
          >
            <label class="emphasis">{{ $t('status.name') }}:</label>
            <HKLSelect
              :disabled="!allowEdit || currentUser.id === $router.currentRoute.params.id"
              :options="userStatusHklOptions"
              :showSearch="false"
              placeholder=""
              :value="userStatusMapping[pendingUpdateUser.enabled]"
              @change="updateStatus"
            />
          </div>
          <label class="emphasis">{{ $t('user.phone') }}:</label>
          <label class="label-content">{{ primaryContactPhoneNumber || 'N/A' }}</label>
          <label class="emphasis">{{ $t('user.email') }}:</label>
          <label
            v-if="!allowEdit"
            class="label-content"
          >
            {{ primaryContactEmail }}
          </label>
          <input
            v-else
            id="user-detail-email-input"
            v-model="primaryContactEmail"
            v-hkldinput
            v-fusioninputcheck="{
              type: 'string',
              pattern: regex.email,
              classes: 'tooltip-class',
              tip: $t('hint.inputTip.email')}"
            type="email"
            class="label-content detail-input"
          >
        </div>
      </div>

      <hr class="hr-style">
      <div>
        <label class="emphasis">{{ $t('settings.dashboard.title') }}:</label>
        <div class="profile-dashboard-radios">
          <label
            v-for="item in defaultDashboardOptions"
            :key="item.key"
          >
            <input
              :id="['modules-settings-engineers-hkl-user-radio-' + item.code + '-dashboard']"
              v-model="pendingUpdateUser.dashboard"
              v-hkldinput
              type="radio"
              :disabled="!allowEdit"
              :value="item.code"
            >
            {{ item.fullName }}
          </label>
        </div>
      </div>

      <hr class="hr-style">

      <div class="row">
        <div class="small-6 medium-6 large-6 columns">
          <label class="emphasis">{{ $t('settings.group') }}:</label>
          <div class="label-content user-roles">
            {{ userRoles }}
          </div>
          <div>
            <ul :class="{ 'ul--disable': !allowEdit }">
              <li
                v-for="(role, index) in roles"
                :key="index"
              >
                <input
                  :id="'modules-settings-engineers-hkl-user-checkbox-' + index"
                  v-model="role.checked"
                  v-hkldinput
                  type="checkbox"
                  :disabled="role.disabled"
                >
                <label
                  class="items"
                  :for="'modules-settings-engineers-hkl-user-checkbox-' + index"
                >
                  {{ role.name === AdminRoleName[0] ? $i18n.t('settings.security.systemAdmin') : role.name }}
                </label>
              </li>
            </ul>
          </div>
          <div v-if="enableTenancyRecordsFeature">
            <label class="emphasis">{{ $t('settings.tenancyAccess') }}:</label>
            <div class="label-content tenancy-access">
              {{ tenancyAccess.join(', ') }}
            </div>
            <div>
              <ul :class="{ 'ul--disable': !allowEdit }">
                <li>
                  <input
                    :id="'modules-settings-engineers-tenancies-checkbox-0'"
                    v-hkldinput
                    type="checkbox"
                    :disabled="!allowEdit"
                    :checked="deep(access, 'tenancies.Office')"
                    @change="event => updateAccess('Office', event.target.checked)"
                  >
                  <label
                    class="items"
                    :for="'modules-settings-engineers-tenancies-checkbox-0'"
                  >
                    {{ translateTenancyType('Office') }}
                  </label>
                </li>
                <li>
                  <input
                    :id="'modules-settings-engineers-tenancies-checkbox-1'"
                    v-hkldinput
                    type="checkbox"
                    :disabled="!allowEdit"
                    :checked="deep(access, 'tenancies.Retail')"
                    @change="event => updateAccess('Retail', event.target.checked)"
                  >
                  <label
                    class="items"
                    :for="'modules-settings-engineers-tenancies-checkbox-1'"
                  >
                    {{ translateTenancyType('Retail') }}
                  </label>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="small-5 medium-5 large-5 columns">
          <label class="emphasis">{{ $t('settings.tab.departmentSiteBuilding') }}:</label>
          <div>
            <ul :class="[{ 'ul--disable': !allowEdit }, 'dept-ul']">
              <li
                v-for="(dept, index) in allDeptLocBld"
                :key="index"
                class="dept-ul-li"
              >
                <i
                  :class="[dept.isExpand ? 'fa-angle-down' : 'fa-angle-right', 'fa angle']"
                  @click="switchArrow(dept)"
                />
                <input
                  :id="'modules-settings-engineers-hkl-user-checkbox-dept' + index"
                  v-hkldinput
                  type="checkbox"
                  :disabled="true"
                  :checked="dept.checked"
                  @change="event => updateDepartment(event.target.checked, dept)"
                >
                <label
                  class="items"
                  :for="'modules-settings-engineers-hkl-user-checkbox-dept' + index"
                >
                  {{ dept.name }}
                </label>
                <ul
                  v-show="dept.isExpand"
                  class="sites-ul"
                >
                  <li>
                    <input
                      :id="'modules-settings-engineers-hkl-user-checkbox-dept-is-admin' + index"
                      v-hkldinput
                      type="checkbox"
                      :disabled="!allowEdit"
                      :checked="dept.isAdmin"
                      @change="event => updateDepartmentAdmin(event.target.checked, dept)"
                    >
                    <label
                      class="items"
                      :for="'modules-settings-engineers-hkl-user-checkbox-dept-is-admin' + index"
                    >
                      {{ dept.name + ' Admin' }}
                    </label>
                  </li>
                  <li
                    v-for="(site, s_index) in dept.sites"
                    :key="site.id + s_index"
                  >
                    <input
                      :id="`modules-settings-engineers-hkl-user-checkbox-site-${site.id}`"
                      v-hkldinput
                      type="checkbox"
                      :disabled="!allowEdit"
                      :checked="site.checked"
                      @change="event => updateSite(event.target.checked, site, dept)"
                    >
                    <label
                      class="items"
                      :for="`modules-settings-engineers-hkl-user-checkbox-site-${site.id}`"
                    >
                      {{ site.code }}
                    </label>
                    <ul class="buildings-ul">
                      <li
                        v-for="(building, b_index) in site.buildings"
                        :key="b_index"
                      >
                        <input
                          :id="`modules-settings-engineers-hkl-user-${site.code}-bld-` + b_index"
                          v-hkldinput
                          type="checkbox"
                          :disabled="!allowEdit"
                          :checked="building.checked"
                          @change="event => updateBuilding(event.target.checked, building, site, dept)"
                        >
                        <label
                          class="items"
                          :for="`modules-settings-engineers-hkl-user-${site.code}-bld-` + b_index"
                        >
                          {{ building.code }}
                        </label>
                      </li>
                    </ul>
                  </li>
                </ul>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import { getCurrentInstance } from 'vue-demi'
import userHelper from '@/utils/userHelper'
import * as regex from '@/utils/regex'
import * as userUtil from '@/api/users/util'
import { aclHelper } from '@/utils/aclHelper'
import { AdminRoleName, UserType, TenderAccountRoleName, ProjectRepAccountRoleName, TenancyRepAccountRoleName } from '@/utils/consts'
import { bShowModConfiguredOption } from '@/utils/dashboardUtils'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { getDepartments } from '@/api/departments/request'
import { getRoles } from '@/api/roles/request'
import { getUser, updateUser, notifyUser } from '@/api/users/request'
import { userStatusOptions, userStatusMapping, userTypeMapping, ENGINEER_DASHBOARDS } from '@/utils/locale'
import { utils as formUtils } from '@/plugin/InputChange'
import HKLButton from '@/controls/Button/HKLButton.vue'
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import dateUtil from '@/utils/dateUtil'

export default {
  name: 'HklUserDetail',
  components: {
    HKLButton,
    HKLSelect,
  },
  setup () {
    const { proxy } = getCurrentInstance()

    const notify = async () => {
      await notifyUser(proxy.userId)
    }
    return {
      notify,
    }
  },

  data () {
    return {
      AdminRoleName,
      deep,
      aclHelper,
      userId: '',
      allowEdit: false,
      userStatusOptions,
      userStatusMapping,
      roles: {},
      locations: {},
      buildings: {},
      user: {},
      pendingUpdateUser: {},
      regex,
      ENGINEER_DASHBOARDS,
      allDeptLocBld: {},
    }
  },
  computed: {
    ...mapGetters('user', ['currentUser']),
    ...mapGetters('regions', ['enableTenancyRecordsFeature', 'enableTenantProjectsFeature', 'enableWorkPermitsFeature', 'enableProjectFeature', 'enableTenderFeature', 'enableDirectoryFeature']),
    userType () {
      return userTypeMapping[this.pendingUpdateUser.usertype]
    },
    userStatusHklOptions () {
      return this.userStatusOptions.map(value => ({
        name: this.$options.filters.customStatusLabel(value),
        value,
      }))
    },
    defaultDashboardOptions () {
      const allDashboardOpts = [
        {
          code: ENGINEER_DASHBOARDS.CMS,
          fullName: this.$t('settings.dashboard.cms'),
        },
        {
          code: ENGINEER_DASHBOARDS.TMS,
          fullName: this.$t('settings.dashboard.tms'),
        },
        {
          code: ENGINEER_DASHBOARDS.WP,
          fullName: this.$t('settings.dashboard.wp'),
        },
      ]
      const enabledDashboardOpts = allDashboardOpts.filter(item => bShowModConfiguredOption.call(this, item.code) ? item : null)
      return enabledDashboardOpts.sort((a, b) => a.fullName.localeCompare(b.fullName))
    },
    primaryContactEmail: {
      get () {
        return (this.pendingUpdateUser.username && userUtil.getPrimaryEmail(this.pendingUpdateUser)) || ''
      },
      set (newEmail) {
        userUtil.updatePrimaryEmailContact(this.pendingUpdateUser, newEmail)
      },
    },
    primaryContactPhoneNumber: {
      get () {
        return (userUtil.getPrimaryPhoneNumberContact(this.pendingUpdateUser).value) || ''
      },
      set (newPhoneNumber) {
        userUtil.updatePrimaryPhoneNumberContact(this.pendingUpdateUser, newPhoneNumber)
      },
    },
    access () {
      return this.pendingUpdateUser.access
    },
    tenancyAccess () {
      const obj = deep(this.user, 'access.tenancies')
      return obj ? Object.keys(obj).filter(type => obj[type]).map(item => this.translateTenancyType(item)) : []
    },
    userRoles () {
      let roles = (this.pendingUpdateUser.roles || [])
      const specialRoleNameMap = {
        [AdminRoleName[0]]: this.$i18n.t('settings.security.systemAdmin'),
        [TenderAccountRoleName[0]]: this.$i18n.t('settings.security.hklTenderRepresentative'),
        [ProjectRepAccountRoleName[0]]: this.$i18n.t('settings.security.hklProjectRepresentative'),
        [TenancyRepAccountRoleName[0]]: this.$i18n.t('settings.security.hklTenancyRepresentative'),
      }
      return roles.map(role => specialRoleNameMap[role.name] || role.name).join(', ')
    },
    userLocations () {
      return this.sortedLocations.map(location => location.code).join(', ')
    },
    sortedLocations () {
      if (this.pendingUpdateUser && this.pendingUpdateUser.locations) {
        return lodash.orderBy(this.pendingUpdateUser.locations, 'code')
      } else {
        return []
      }
    },
    formattedLastLoginAt () {
      return dateUtil.formatDate(this.pendingUpdateUser.lastLoginAt, true)
    },
  },
  async mounted () {
    await this.init()
  },

  methods: {
    ...mapActions('user', ['reloadUser']),
    async init () {
      this.userId = this.$router.currentRoute.params.id
      await this.initUser()
      await this.initAllRoles()
      await this.initAllDeptLocBld()
      this.fillUpUserInfos()
    },
    async initUser () {
      const { user } = await getUser(this.userId)
      this.pendingUpdateUser = user
      this.pendingUpdateUser.access = this.pendingUpdateUser.access || {}
      this.user = lodash.cloneDeep(this.pendingUpdateUser)
    },
    filterModConfiguredOptions (code) {
      switch (code) {
        case ENGINEER_DASHBOARDS.CMS:
          return this.enableTenderFeature && this.enableProjectFeature
        case ENGINEER_DASHBOARDS.TMS:
          return this.enableTenantProjectsFeature && this.enableDirectoryFeature
        case ENGINEER_DASHBOARDS.WP:
          return this.enableWorkPermitsFeature
        default:
          return false
      }
    },
    userName (user) {
      if (user.username) {
        const name = userHelper.getUserName(user.username, user.displayName)
        return name
      }
    },
    translateTenancyType (tenancyType) {
      return this.$t(`tenancy.${tenancyType.toLowerCase()}`)
    },
    switchArrow (dept) {
      dept.isExpand = !dept.isExpand
    },
    setAllToUnchecked (items, itemName, childItemsName) {
      const uncheckedItems = {}
      const uncheckedChildItems = {}

      items.forEach((item) => {
        item.checked = false
        uncheckedItems[item.id] = item

        if (childItemsName && Array.isArray(item[childItemsName])) {
          item[childItemsName].forEach((childItem) => {
            childItem.checked = false
            uncheckedChildItems[childItem.id] = childItem
          }, uncheckedChildItems)
        }
      }, uncheckedItems)

      this[itemName] = uncheckedItems
      if (childItemsName) {
        this[childItemsName] = uncheckedChildItems
      }
    },

    async initAllRoles () {
      const roles = await getRoles()
      if (!roles) {
        return
      }
      const specialRoleMap = [
        {
          usertype: UserType.Staff.TenderAccount,
          targetName: TenderAccountRoleName[0],
          newName: this.$i18n.t('settings.security.hklTenderRepresentative'),
        },
        {
          usertype: UserType.Staff.ProjectRep,
          targetName: ProjectRepAccountRoleName[0],
          newName: this.$i18n.t('settings.security.hklProjectRepresentative'),
        },
        {
          usertype: UserType.Staff.TenancyRep,
          targetName: TenancyRepAccountRoleName[0],
          newName: this.$i18n.t('settings.security.hklTenancyRepresentative'),
        },
      ]
      for (const roleObj of specialRoleMap) {
        const index = roles.findIndex(item => item.usertype === roleObj.usertype)
        if (index !== -1) {
          const role = roles.splice(index, 1)[0]
          if (role.name === roleObj.targetName) {
            role.name = roleObj.newName
          }
          roles.push(role)
        }
      }
      this.setAllToUnchecked(roles, 'roles')
    },
    async initAllDeptLocBld () {
      const allDeptLocBld = await getDepartments({sites: true, buildings: true})
      if (!allDeptLocBld || !allDeptLocBld.length) {
        return null
      }
      allDeptLocBld.forEach(dept => {
        dept.checked = false
        dept.isAdmin = false
        if (!dept.sites) return
        dept.sites.forEach(site => {
          site.checked = false
          if (!site.buildings.length) return
          site.buildings.forEach(building => {
            building.checked = false
          })
        })

        dept.isExpand = true
      })
      this.allDeptLocBld = allDeptLocBld
    },
    updateDepartment (checked, dept) {
      dept.checked = checked
      if (!checked) {
        dept.isAdmin = this.unCheckOneDept(dept)
      }
    },
    unCheckOneDept (dept) {
      dept.isAdmin = false
      dept.sites.map(site => { this.unCheckOneSite(site) })
    },
    uncheckDept (dept) {
      const isExsitingSite = dept.sites.map(site => site.checked).includes(true)
      if (dept.isAdmin || isExsitingSite) return
      dept.checked = false
    },
    updateDepartmentAdmin (checked, dept) {
      dept.isAdmin = checked
      if (checked) {
        dept.checked = true
      } else {
        this.uncheckDept(dept)
      }
    },
    updateSite (checked, site, dept) {
      site.checked = checked
      if (checked) {
        dept.checked = true
      } else {
        this.unCheckOneSite(site, dept)
      }
    },
    unCheckOneSite (site, dept) {
      site.checked = false
      site.buildings.map(building => { building.checked = false })
      this.uncheckDept(dept)
    },
    updateBuilding (checked, building, site, dept) {
      building.checked = checked
      if (checked) {
        site.checked = true
        dept.checked = true
      }
    },

    updateAccess (attr, checked) {
      if (!this.access.tenancies) {
        this.access.tenancies = {}
      }
      this.access.tenancies[attr] = checked
    },

    setChecked (toItemName) {
      const fromItems = this.pendingUpdateUser[toItemName] || []
      const fromItemIds = fromItems.map(item => item.id)
      Object.values(this[toItemName]).forEach(item => {
        item.checked = fromItemIds.includes(item.id)
      })
    },
    setDisabled (toItemName) {
      const commonDisabled = !this.pendingUpdateUser.enabled || !this.allowEdit
      lodash.mapValues(this[toItemName], (item) => {
        item.disabled = commonDisabled ||
          item.name === AdminRoleName[0] ||
          item.usertype === UserType.Staff.TenderAccount ||
          item.usertype === UserType.Staff.ProjectRep ||
          item.usertype === UserType.Staff.TenancyRep
        return item
      })
    },

    setAllDeptChecked () {
      let userDepts = this.pendingUpdateUser.departments.map(dept => {
        return { id: dept.id, isAdmin: dept.UserDepartment.isAdmin }
      })
      let userSiteIds = this.pendingUpdateUser.locations.map(location => location.id)
      let userBuildings = this.pendingUpdateUser.buildings.map(building => {
        return { id: building.id, code: building.code, locationId: building.UserLocationBuilding.locationId }
      })
      if (!this.allDeptLocBld.length) return
      for (const dept of this.allDeptLocBld) {
        let userDept = userDepts.find(item => item.id === dept.id)
        dept.checked = !!userDept
        dept.isAdmin = userDept ? userDept.isAdmin : false
        for (const site of dept.sites) {
          site.checked = dept.checked && userSiteIds.includes(site.id)
          for (const building of site.buildings) {
            building.checked = site.checked &&
            userBuildings.some(item => item.id === building.id && item.locationId === site.id)
          }
        }
      }
    },

    fillUpUserInfos () {
      this.setChecked('roles')
      this.setDisabled('roles')
      this.setAllDeptChecked()
    },

    processPendingUserField (fieldName) {
      const checkedEntries = Object.values(this[fieldName] || {})
        .filter(entry => entry.checked)
      this.pendingUpdateUser[fieldName] = checkedEntries
      return checkedEntries
    },
    processPendingAllDeptLocBld () {
      let updateDepts = []
      let updateLocs = []
      let updateBlds = []
      if (!this.allDeptLocBld.length) return { updateDepts, updateLocs, updateBlds }
      this.allDeptLocBld.forEach(dept => {
        if (!dept.checked) return
        updateDepts.push({ id: dept.id, isAdmin: dept.isAdmin })
        if (!dept.sites.length) return
        dept.sites.forEach(site => {
          if (!site.checked) return
          updateLocs.push(site.id)
          if (!site.buildings.length) return
          site.buildings.forEach(building => {
            if (building.checked) {
              updateBlds.push({ id: building.id, locationId: site.id })
            }
          })
        })
      })
      return { updateDepts, updateLocs, updateBlds }
    },
    updateUserRolesAndLocations () {
      const updateRoles = this.processPendingUserField('roles')
      const { updateDepts, updateLocs, updateBlds } = this.processPendingAllDeptLocBld()

      return {
        roles: updateRoles.map(entry => entry.id),
        departments: updateDepts,
        locations: updateLocs,
        buildings: updateBlds,
        access: this.access,
        enabled: this.pendingUpdateUser.enabled,
      }
    },

    updateStatus (selectOption) {
      formUtils.setDirty()
      if (selectOption === this.userStatusOptions[0]) {
        this.pendingUpdateUser.enabled = true
      } else {
        this.pendingUpdateUser.enabled = false
        this.allDeptLocBld.forEach(dept => { dept.isAdmin = false; this.uncheckDept(dept) })
      }
    },

    enableEdit () {
      this.allowEdit = true
      this.user = lodash.cloneDeep(this.pendingUpdateUser)
      this.setDisabled('roles')
    },

    discardChange () {
      this.pendingUpdateUser = this.user
      this.fillUpUserInfos()
      this.allowEdit = false
      formUtils.resetDirty()
    },
    validationEmail () {
      fusionInputCheckUtil.validateAllFields({allowEmpty: 'none'}, '.user-detail-wrapper')
      return fusionInputCheckUtil.isAllFieldsValid('.user-detail-wrapper')
    },
    async save () {
      if (!this.pendingUpdateUser) {
        return
      }
      if (this.validationEmail()) {
        try {
          const user = this.updateUserRolesAndLocations()
          user.contacts = this.pendingUpdateUser.contacts
          user.dashboard = this.pendingUpdateUser.dashboard
          await updateUser(this.userId, { user })
          this.allowEdit = false
          this.user = JSON.parse(JSON.stringify(this.pendingUpdateUser))
          if (this.currentUser.id === this.userId) {
            await this.reloadUser({ adId: this.currentUser.AD_id })
          }
          await this.init()
          formUtils.resetDirty()
        } catch (err) {
          this.pendingUpdateUser = null
          this.pendingUpdateUser = this.user
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/settings/style';

.nav-section{
  padding: 0 24px 10px 24px;
  border-bottom: 1px solid #e0e0e0;
  a {
    padding-left: 0;
    font-size: 14px;
  }
}
.user-detail-wrapper {
  padding: 20px 24px;
  label {
    margin-top: 0;
  }

}
.user-detail-header-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
}

.last-login-info {
  flex: 1;
  min-width: 0;
}

.header-controls {
  flex-shrink: 0;
  width: auto;
  padding-left: 16px;
}

.right-adjusted {
  text-align: right;
  button {
    display: inline;
  }
}

ul, .ul--disable {
  list-style-type: none;
  width: 60%;
  margin: 8px 0;
}
ul {
  border: solid 1px #5F7988;
}
.ul--disable {
  border: solid 1.2px #BAC3CA;
}

.dept-ul {
  border: none;
  margin: 35px 0 8px 0px;
  width: auto;
  .dept-ul-li {
    margin-left: 0;
    i {
      font-size: 20px;
      vertical-align: middle;
    }
  }
}
.sites-ul {
  width: auto;
  border: none;
  margin: 0 0 8px 20px;
}

.buildings-ul {
  border: none;
  margin: 0 0 8px 8px;
}

li {
  margin-left: 8px;
}

.row {
  margin-bottom: 8px;
}

.row + .row {
  margin-top: 10px;
}

.detail-input {
  width: 66.7%;
  padding: 1vh 0.5vw;
  color: #0C3A58;
  border: 1px solid #5f7988;
}

.label-content {
  margin-top: 5px;
  font-weight: normal;
}

.hr-style {
  margin: 20px 0;
}

.items {
  margin: 5px 8px;
}
.user-roles, .user-locations .tenancy-access {
  width: calc(100% - 12px);
}

.tenancy-access {
  text-transform: capitalize;
}
.username-content {
  margin-bottom: 20px;
}

.position-content {
  overflow-wrap: anywhere;
  word-break: break-word;
}
</style>
